/**
 * Issue #683 — THE UNIFIED DIRECTORY: global/verified + mine + everywhere
 * played (+ saved), AND ARCHIVED VENUES OFF THE PLAYER'S MAP WITH ONE
 * EXCEPTION.
 *
 * ## Why this is a lib file and not inline in `GroundsMap.jsx`
 *
 * The same two reasons `useDirectoryViewport`/`useDirectorySearch` were
 * pulled out for (#643/#644), and this slice hit the same wall: the
 * component function is measured against `max-lines-per-function: 710`
 * (`frontend/eslint.config.mjs`, whose own header records that the number is
 * never raised to make a new function fit), and folding two more personal
 * sources into the merge pushed it over. This is an ordinary, pure function
 * — no hooks, no React — so it moves out exactly like the arithmetic in
 * `lib/directorySearch.js` and `lib/mapViewport.js` did, and gets an
 * ordinary test instead of only ever being checked by grepping the page's
 * source.
 *
 * ## What changed, and what did not
 *
 * `rows` (the directory read/search) no longer carries archived venues for
 * anyone but an admin — `useDirectoryViewport`/`useDirectorySearch` stopped
 * asking for them at all (`lib/directoryLoad.js`'s `directoryBasePath`).
 * That is correct for a venue this player has no relationship with: an
 * archived venue answers nothing they asked, and showing it with no way to
 * act on it is information with no remedy attached.
 *
 * It is NOT correct for a venue they saved or played at, which is why the
 * `playedOnly` fold below (pre-existing, since #57) and the `savedOnly` fold
 * beside it (new here) both exist: `/venues/played` and `/players/me/grounds`
 * are PERSONAL and HISTORICAL reads that keep archived rows on purpose —
 * `lib/archived.js`'s header states the same rule for the picker's
 * suggestion sources, and this is that rule's extension to the map.
 *
 * ## THE PRINCIPLE THAT MAKES THIS CONSISTENT RATHER THAN A CONTRADICTION
 *
 *   A badge is justified where the player had a prior expectation to
 *   explain, and unjustified where they did not.
 *
 * "Archived" on a venue you saved or played at answers a real question —
 * where did my saved ground go? — because you already knew it was there. On
 * a venue you never touched, it answers nothing you asked, which is exactly
 * why that row is dropped rather than shown archived. Decided by
 * Martin/Andreas on #683: option (b).
 *
 * ## The BLOCKER this fold depends on: `archived_at` must travel
 *
 * `GET /api/venues/played` has always returned `archived_at`
 * (`api/src/routes/venues.ts`), and `GET /players/me/grounds` has always
 * returned it too (`api/src/routes/players.ts`'s `toApiBookmarkedGround`).
 * Once archived rows leave the directory read, these two synthetic folds are
 * the ONLY way an archived venue a player touched reaches the map at all —
 * without carrying the field through, either fold renders as an ordinary,
 * unbadged, live pin, and (b)'s promise ("the ones you've touched stay,
 * badged") silently fails for that half. Both folds below carry it.
 */
import { matchesSearchTerm } from "./directorySearch";
import { boxContainsPoint } from "./mapViewport";

/**
 * Issue #683, the fix — THE FOLD, MADE VIEWPORT-AWARE (issue #688 later
 * removed the MODE half of that, below).
 *
 * The salvaged first cut of this file folded `playedOnly`/`savedOnly`
 * unconditionally: no `viewport` check at all, so a saved or played venue
 * outside the box on screen could still drag the map's own fit to somewhere
 * the user was not looking. #683 also gated the fold on `mode` — no fold at
 * all while the map was aggregated (cells) — which #688 then reversed: see
 * `foldSuppressed`'s own header for why a player's saved/played grounds must
 * survive a zoomed-out map rather than disappear with the row-level read.
 *
 * `searching` and `pending` are two of the exact names
 * `lib/directoryLoad.js`'s `directoryOutcome` already takes, passed through
 * unchanged from `GroundsMap.jsx` rather than reinvented here — that
 * function's own rule ("while a search is active the viewport's mode has
 * nothing to do with it"; "a question with no answer yet is not an answer")
 * is exactly the rule this fold needs too, so it is followed rather than
 * duplicated in a second shape.
 */
export function enrichDirectory({
  rows,
  playedVenues,
  savedVenues,
  statsByName,
  savedIds,
  term,
  searching,
  pending,
  viewportBox,
}) {
  const playedByGroundId = new Map();
  playedVenues.forEach((v) => {
    if (v.ground_id) playedByGroundId.set(v.ground_id, v);
  });

  const toEntry = (g, played) => {
    const stats = played ? statsByName.get((played.ground || g.name || "").toLowerCase()) : null;
    return {
      ...g,
      // Issue #677 — the server now computes this directly (`is_mine`,
      // `g.createdBy === caller.id`) rather than handing every reader the
      // raw creator id to compare themselves. `!!` guards the synthetic
      // "played-only"/"saved-only" rows below, which set `is_mine: null`
      // rather than omitting it.
      _authored: !!g.is_mine,
      _saved: savedIds.has(g.ground_id),
      _played: !!played,
      _stats: played ? { matches: played.matches, ...(stats || {}) } : null,
    };
  };

  const fromDirectory = rows.map((g) => toEntry(g, playedByGroundId.get(g.ground_id) || null));

  // A played venue whose matching ground isn't in this caller's visible
  // directory (e.g. a private ground another user created, enriched
  // read-time-only by name in listVenuesPlayedForPlayer) still surfaces as a
  // read-only "played" pin — that's what "everywhere you've played" means in
  // the unified view.
  const directoryIds = new Set(rows.map((g) => g.ground_id));

  /**
   * Issue #683 — WHEN THE FOLD CONTRIBUTES NOTHING AT ALL.
   *
   * This USED TO also suppress the whole fold whenever `mode === "cells" &&
   * !searching` — journey 26 had caught two auto-bookmarked venues folded
   * onto a map that was showing nothing but count bubbles, and blanking the
   * fold in that state was the fix at the time.
   *
   * Issue #688 reverses that half: Martin's report is a player at world zoom
   * whose SAVED and PLAYED grounds no longer appear at all, because cells
   * mode returns no rows for anything else to flag. #642's "a box that
   * aggregates draws no ground cards" is a rule about the DIRECTORY read —
   * it was never meant to reach into a player's own personal list and empty
   * that too. So the cells-mode branch is gone: a saved or played venue now
   * folds in at any zoom, subject only to the viewport/search gates below,
   * and `GroundsMap.jsx`'s `DirectoryGridEmpty` carries the coexistence rule
   * journey 26 actually needed — a line under the grid saying the personal
   * rows are the whole answer and the rest wants a zoom, rather than an
   * empty fold pretending the player has nothing saved.
   *
   * `pending` is the one case that still blanks the fold outright. This fold
   * reads `savedVenues`/`playedVenues` against the CURRENT `term` with no
   * debounce of its own, so while a real server search for that term is
   * still in flight it can answer first — before `useDirectorySearch`'s own
   * `pending` clears. A player's own OWN card can then appear a render or
   * two before `mapBlockOrderClass` (`lib/searchResultSummary.js`) reorders
   * the list above the map, because that reorder is deliberately gated on
   * the same `pending` flag clearing. Journey 1b caught the two racing: the
   * fold's card was already on screen, the reorder had not run yet, and a
   * single-shot geometry read landed the matched card 584px below an
   * 844px phone's fold. Suppressing the fold while `pending` puts the card
   * and the reorder back on the same clock — both wait for the same answer.
   */
  const foldSuppressed = Boolean(pending);

  /**
   * Issue #683 — WHEN THE FOLD CONTRIBUTES ONLY WHAT IS ON SCREEN.
   *
   * While the map is showing its own viewport (`!searching`), a played/saved
   * venue outside the box currently on screen must not widen the map's own
   * FIT (`lib/mapFit.js`) to somewhere the user is not looking — journey 15
   * caught this dragging the fit to a point between Wellington and London
   * that was neither, which then poisoned the fit's own change-detection so
   * a LATER, legitimate search-fit was silently skipped.
   *
   * While a search IS active the box does not apply: #644's search-fit is
   * explicitly a question about the whole directory rather than the screen,
   * and journey 28 depends on a saved/played archived venue surfacing
   * through search regardless of where the map happens to be looking — the
   * TERM is what scopes it there (`matchesSearchTerm` below), same as it
   * always has.
   *
   * `viewportBox` absent (not yet loaded, or a caller — e.g. every existing
   * unit test — that never passes one) means "no box to check", so nothing
   * is filtered on this basis: the pre-#683-fix, unconditional behaviour.
   */
  const inViewport = (v) => searching || !viewportBox || boxContainsPoint(viewportBox, v.latitude, v.longitude);

  // A played venue whose matching ground isn't in this caller's visible
  // directory (e.g. a private ground another user created, enriched
  // read-time-only by name in listVenuesPlayedForPlayer) still surfaces as a
  // read-only "played" pin — that's what "everywhere you've played" means in
  // the unified view.
  const playedOnly = foldSuppressed
    ? []
    : playedVenues
        .filter((v) => v.ground_id && !directoryIds.has(v.ground_id) && v.latitude != null && v.longitude != null)
        // #644 — AND, WHILE A SEARCH IS ACTIVE, MATCHING IT. These rows come
        // from `/venues/played`, not from the directory read, so no `q` sent to
        // `GET /api/grounds` can ever return or exclude them: they are outside
        // its visibility scope by construction. Before this slice the page's own
        // term filter removed the non-matching ones; without this line they
        // leaked into every search result, and journey 1 caught it — searching
        // a London ground fitted the map to a box holding London AND the
        // Auckland ground the player had played at, centring the view in the
        // Atlantic. See `matchesSearchTerm` for why this one client-side
        // substring is not the thing #644 removes.
        .filter((v) => matchesSearchTerm({ name: v.ground, city: v.city, country: v.country }, term))
        // Issue #683 — AND, WHEN THE MAP IS LOOKING AT ITS OWN VIEWPORT,
        // INSIDE IT. See `inViewport`'s own header above.
        .filter(inViewport)
        .map((v) =>
          toEntry(
            {
              ground_id: v.ground_id,
              name: v.ground,
              city: v.city,
              country: v.country,
              latitude: v.latitude,
              longitude: v.longitude,
              // Issue #677 — this synthetic row never came from `GET
              // /api/grounds`, so there is no server-computed `is_mine` to
              // carry; `false` preserves the old `created_by: null` row's
              // behaviour (never counted as "mine").
              is_mine: false,
              is_global: false,
              is_verified: false,
              created_at: null,
              // Issue #136: whatever the API could resolve, which is `null` when
              // the free-text match ground matched no directory row.
              place_type: v.place_type ?? null,
              // Issue #683 — THE BLOCKER. See the module header: without this,
              // a played-at archived venue renders as an ordinary live pin.
              archived_at: v.archived_at ?? null,
            },
            v,
          ),
        );

  // Issue #683 — THE SAME FOLD, FOR SAVED VENUES.
  //
  // `savedVenues` (`/players/me/grounds`) already carries `archived_at` for
  // the identical reason `playedVenues` does (`lib/archived.js`): a bookmark
  // is the player's own pointer, and it must not vanish along with the
  // directory read that used to carry it. This is smaller than the played
  // fold above — a bookmarked ground is never a free-text match, so there is
  // no `place_type` to guess at and no per-ground stat to look up — but it
  // exists for exactly the same reason and is deduplicated against BOTH the
  // directory rows and the played-only fold above, so a venue that is saved
  // AND played still draws exactly one pin.
  const playedOnlyIds = new Set(playedOnly.map((entry) => entry.ground_id));
  const savedOnly = foldSuppressed
    ? []
    : savedVenues
        .filter(
          (v) =>
            v.ground_id &&
            !directoryIds.has(v.ground_id) &&
            !playedOnlyIds.has(v.ground_id) &&
            v.latitude != null &&
            v.longitude != null,
        )
        // #644's rule, restated for a second personal source: this row cannot
        // come from `GET /api/grounds?q=`, so it is matched against an active
        // search term here, the same way `playedOnly` is just above.
        .filter((v) => matchesSearchTerm({ name: v.name, city: v.city, country: v.country }, term))
        // Issue #683 — same viewport gate as `playedOnly` above.
        .filter(inViewport)
        .map((v) =>
          toEntry(
            {
              ground_id: v.ground_id,
              name: v.name,
              city: v.city,
              country: v.country,
              latitude: v.latitude,
              longitude: v.longitude,
              is_mine: false,
              is_global: v.is_global,
              is_verified: v.is_verified,
              created_at: null,
              place_type: v.place_type ?? null,
              archived_at: v.archived_at ?? null,
            },
            // A saved venue can ALSO be a played one; look the match history up
            // by id so `_played`/`_stats` still populate rather than reading as
            // an ordinary bookmark on a venue this player has actually played.
            playedByGroundId.get(v.ground_id) || null,
          ),
        );

  return [...fromDirectory, ...playedOnly, ...savedOnly];
  // #644: `rows`, not a whole-directory array — the rows in hand are the
  // SEARCH's answer while a term is active and the viewport's otherwise.
  // Issue #677 — no `user` is read here at all: `_authored` reads the
  // server-computed `g.is_mine` instead of comparing `g.created_by` against
  // a caller id.
}
